import * as Sentry from '@sentry/browser';
import { SeverityLevel } from '@sentry/browser';
import Events, { Traits } from './schema';
import SegmentMonitor from './SegmentMonitor';

/**
 * analytics.identify
 * @param id
 * @param traits
 */
const identify = (id: string | number | undefined, traits?: unknown): void => {
  if (window.analytics) {
    window.analytics.identify(id, traits as never);
  } else {
    Sentry.captureMessage('identify called before window.analytics available', {
      level: 'warning' as SeverityLevel,
    });
  }
};

/**
 * analytics.track
 * @param name
 * @param properties
 */
const track = <T>(name: string, properties?: T): void => {
  if (window.analytics) {
    window.analytics.track(name, properties as never);
  } else {
    Sentry.captureMessage('track called before window.analytics available', {
      level: 'warning' as SeverityLevel,
    });
  }
};

/**
 * analytics.page
 * @param url
 */
const page = (url: string): void => {
  if (window.analytics) {
    window.analytics.page(url, {
      screen: {
        width: window.screen.width,
        height: window.screen.height,
      },
      window: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    });
  } else {
    Sentry.captureMessage('page called before window.analytics available', {
      level: 'warning' as SeverityLevel,
    });
  }
};

/**
 * Get local version of analytics.js traits from localstorage
 */
export const getLocalTraits = (): Traits | undefined => {
  const ajsUserTraits = localStorage.getItem('ajs_user_traits');
  if (ajsUserTraits) {
    try {
      return JSON.parse(ajsUserTraits);
    } catch (error) {
      return undefined;
    }
  } else {
    return undefined;
  }
};

/**
 * Is a trait different from the current trait saved
 * @param property
 * @param value
 */
export const isTraitDifferent = (property: string, value: unknown) => {
  const localTraits = getLocalTraits();
  return !(localTraits && localTraits[property as keyof typeof localTraits] === value);
};

export { SegmentMonitor, identify, track, page, Events };
